export type LoanCalculatorFormState = {
  downPayment: string;
  years: string;
  nominalInterestRate?: number;
  monthlyPayment?: number;
};

export type SubmittedLoanCalculatorValues = {
  downPayment?: number;
  years?: number;
  nominalInterestRate?: number;
  monthlyPayment?: number;
};

export const NOMINAL_ANNUAL_INTEREST_RATE = 0.0725;
export const ESTABLISHMENT_FEE = 4540;
export const MONTHLY_FEE = 95;

export const DEFAULT_LOAN_CALCULATOR_YEARS_OPTION = "7";

export const getLoanCalculatorInitialState = (
  carPrice?: number,
): LoanCalculatorFormState => ({
  downPayment:
    carPrice && carPrice > 0 ? String(Math.round(carPrice * 0.1)) : "",
  years: DEFAULT_LOAN_CALCULATOR_YEARS_OPTION,
});

export const loanCalculatorInitialState = getLoanCalculatorInitialState();

const sanitizeNumericInput = (value: string) => value.replace(/\D/g, "");

export const parseCurrencyInput = (value: string) => {
  const digits = sanitizeNumericInput(value);
  return digits ? Number(digits) : 0;
};

export const calculateEffectiveAnnualInterestRate = ({
  loanAmount,
  monthlyPayment,
  years,
}: {
  loanAmount: number;
  monthlyPayment: number;
  years: number;
}) => {
  if (loanAmount <= 0 || monthlyPayment <= 0 || years <= 0) {
    return null;
  }

  const months = years * 12;
  const presentValueAtRate = (monthlyRate: number) => {
    if (monthlyRate === 0) {
      return monthlyPayment * months;
    }

    return (
      (monthlyPayment * (1 - Math.pow(1 + monthlyRate, -months))) / monthlyRate
    );
  };

  let lowerBound = 0;
  let upperBound = 1;

  while (presentValueAtRate(upperBound) > loanAmount) {
    upperBound *= 2;

    if (upperBound > 100) {
      return null;
    }
  }

  for (let index = 0; index < 60; index += 1) {
    const midpoint = (lowerBound + upperBound) / 2;
    const presentValue = presentValueAtRate(midpoint);

    if (presentValue > loanAmount) {
      lowerBound = midpoint;
    } else {
      upperBound = midpoint;
    }
  }

  const effectiveMonthlyRate = (lowerBound + upperBound) / 2;

  return Math.pow(1 + effectiveMonthlyRate, 12) - 1;
};

export const calculateMonthlyPayment = ({
  nominalAnnualInterestRate,
  establishmentFee,
  loanAmount,
  monthlyFee,
  years,
}: {
  nominalAnnualInterestRate: number;
  establishmentFee: number;
  loanAmount: number;
  monthlyFee: number;
  years: number;
}) => {
  if (loanAmount <= 0 || years <= 0) {
    return 0;
  }

  const months = years * 12;
  const monthlyInterestRate = nominalAnnualInterestRate / 12;

  return (
    ((loanAmount + establishmentFee) * monthlyInterestRate) /
      (1 - Math.pow(1 + monthlyInterestRate, -months)) +
    monthlyFee
  );
};

export const getSubmittedLoanCalculatorValues = (
  values: LoanCalculatorFormState,
): SubmittedLoanCalculatorValues | undefined => {
  const sanitizedDownPayment = sanitizeNumericInput(values.downPayment);
  const parsedYears = Number(values.years);
  const hasDownPayment = sanitizedDownPayment.length > 0;
  const hasYears = Number.isFinite(parsedYears) && parsedYears > 0;

  if (!hasDownPayment && !hasYears) {
    return undefined;
  }

  return {
    ...(hasDownPayment ? { downPayment: Number(sanitizedDownPayment) } : {}),
    ...(hasYears ? { years: parsedYears } : {}),
    ...(values.nominalInterestRate != null
      ? { nominalInterestRate: values.nominalInterestRate }
      : {}),
    ...(values.monthlyPayment != null
      ? { monthlyPayment: values.monthlyPayment }
      : {}),
  };
};
